/**
 * TransactionFieldDialog Component
 *
 * Dialog for adding or editing a transaction custom field: label, icon, type (text/attachment),
 * and which transaction type it applies to (expense, income, or transfer). One field = one type.
 * Transaction type (Expense/Income/Transfer) cannot be changed after creation. Icon can be changed anytime. Save is disabled in edit mode until user changes something.
 */

import { ReactElement, useEffect, useMemo, useRef, useState } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  FormHelperText,
  Box,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ReceiptIcon from "@mui/icons-material/Receipt";
import NoteIcon from "@mui/icons-material/Note";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DescriptionIcon from "@mui/icons-material/Description";
import LabelIcon from "@mui/icons-material/Label";
import InventoryIcon from "@mui/icons-material/Inventory";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import NumbersIcon from "@mui/icons-material/Numbers";
import StandardDialog from "../../../components/StandardDialog";
import CannotChangeLaterIcon from "../../../components/CannotChangeLaterIcon";
import type {
  TransactionCustomField,
  TransactionCustomFieldType,
  TransactionCustomFieldScopeType,
  TransactionCustomFieldIconName,
} from "../../../types";

const SCOPE_OPTIONS: { value: TransactionCustomFieldScopeType; label: string }[] = [
  { value: "expense", label: "Expense" },
  { value: "income", label: "Income" },
  { value: "transfer", label: "Transfer" },
];

const NoIconPlaceholder = () => <Box component="span" sx={{ width: 24, height: 24, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "text.secondary", fontSize: "0.75rem" }}>—</Box>;

const ICON_OPTIONS: { value: TransactionCustomFieldIconName; label: string; Icon: React.ElementType }[] = [
  { value: "no_icon", label: "No icon", Icon: NoIconPlaceholder },
  { value: "receipt", label: "Receipt", Icon: ReceiptIcon },
  { value: "note", label: "Note", Icon: NoteIcon },
  { value: "attach_file", label: "Attachment", Icon: AttachFileIcon },
  { value: "description", label: "Description", Icon: DescriptionIcon },
  { value: "tag", label: "Tag", Icon: LabelIcon },
  { value: "invoice", label: "Invoice", Icon: InventoryIcon },
  { value: "picture_as_pdf", label: "PDF", Icon: PictureAsPdfIcon },
  { value: "numbers", label: "Numbers", Icon: NumbersIcon },
];

const DEFAULT_ICON: TransactionCustomFieldIconName = "description";

interface TransactionFieldDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    label: string;
    type: TransactionCustomFieldType;
    transactionType: TransactionCustomFieldScopeType;
    icon?: TransactionCustomFieldIconName;
  }) => void;
  editingField: TransactionCustomField | null;
  saving: boolean;
  /** All existing fields (visible + hidden) to check for duplicate labels within the same transaction type */
  existingFields: TransactionCustomField[];
  /** When adding (not editing), preselect this transaction type (e.g. from section + button). */
  initialTransactionType?: TransactionCustomFieldScopeType;
}

function normalizeLabel(s: string): string {
  return s.trim().toLowerCase();
}

export function TransactionFieldDialog({
  open,
  onClose,
  onConfirm,
  editingField,
  saving,
  existingFields,
  initialTransactionType,
}: TransactionFieldDialogProps): ReactElement {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<TransactionCustomFieldType>("text");
  const [transactionType, setTransactionType] = useState<TransactionCustomFieldScopeType>("expense");
  const [icon, setIcon] = useState<TransactionCustomFieldIconName>(DEFAULT_ICON);
  /** When creating, check duplicates against fields at open time so we don't show "already exists" after submit. */
  const existingFieldsWhenOpenedRef = useRef<TransactionCustomField[]>([]);

  /** Resolve which transaction type this field belongs to (supports legacy transactionTypes). */
  const fieldTransactionType = (f: TransactionCustomField): TransactionCustomFieldScopeType =>
    f.transactionType ?? f.transactionTypes?.[0] ?? "expense";

  useEffect(() => {
    if (open) {
      if (editingField) {
        setLabel(editingField.label);
        setType(editingField.type);
        setTransactionType(fieldTransactionType(editingField));
        setIcon(
          editingField.icon !== undefined && ICON_OPTIONS.some((o) => o.value === editingField.icon)
            ? (editingField.icon as TransactionCustomFieldIconName)
            : DEFAULT_ICON
        );
      } else {
        setLabel("");
        setType("text");
        setTransactionType(initialTransactionType ?? "expense");
        setIcon(DEFAULT_ICON);
        existingFieldsWhenOpenedRef.current = existingFields;
      }
    }
  }, [open, editingField, existingFields, initialTransactionType]);

  const trimmedLabel = label.trim();
  const fieldsToCheck = editingField ? existingFields : existingFieldsWhenOpenedRef.current;
  /** Duplicate check only within the same transaction type (same name allowed across types). */
  const sameTypeFields = fieldsToCheck.filter((f) => fieldTransactionType(f) === transactionType);
  const duplicateVisible = sameTypeFields.find(
    (f) => !f.hidden && f.id !== editingField?.id && normalizeLabel(f.label) === normalizeLabel(trimmedLabel)
  );
  const duplicateHidden = sameTypeFields.find(
    (f) => f.hidden && f.id !== editingField?.id && normalizeLabel(f.label) === normalizeLabel(trimmedLabel)
  );
  const duplicateMessage =
    duplicateVisible
      ? "A field with this name already exists for this transaction type."
      : duplicateHidden
        ? "You have this in the hidden list for this type. Unhide it to use it."
        : null;
  const hasDuplicate = Boolean(duplicateVisible || duplicateHidden);

  const handleConfirm = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    onConfirm({
      label: trimmed,
      type,
      transactionType,
      icon,
    });
  };

  const typeLabelId = "transaction-field-type-label";
  const typeSelectValue = type === "text" ? "text" : "attachment";
  const isEditMode = Boolean(editingField);

  /** In edit mode, Save is only enabled when the user has changed something (e.g. label or icon). */
  const hasChanges = useMemo(() => {
    if (!editingField) return true;
    const labelChanged = label.trim() !== editingField.label;
    const effectiveOriginalIcon = (editingField.icon ?? DEFAULT_ICON) as TransactionCustomFieldIconName;
    const iconChanged = icon !== effectiveOriginalIcon;
    return labelChanged || iconChanged;
  }, [editingField, label, icon]);

  return (
    <StandardDialog
      open={open}
      onClose={onClose}
      title={editingField ? "Edit transaction field" : "Add transaction field"}
      confirmText={saving ? "Saving…" : editingField ? "Save" : "Add"}
      onConfirm={handleConfirm}
      confirmDisabled={
        saving ||
        !label.trim() ||
        hasDuplicate ||
        (isEditMode && !hasChanges)
      }
      maxWidth="sm"
    >
      <Stack spacing={2}>
        <TextField
          autoFocus
          margin="dense"
          label="Label"
          fullWidth
          variant="outlined"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={saving}
          placeholder="e.g. Invoice number, Receipt"
          error={hasDuplicate && trimmedLabel.length > 0}
          helperText={duplicateMessage}
          FormHelperTextProps={{ sx: { color: "error.main" } }}
        />
        <FormControl fullWidth variant="outlined" size="medium" sx={{ position: "relative" }}>
          <InputLabel id={typeLabelId}>Type</InputLabel>
          <Select<TransactionCustomFieldType>
            labelId={typeLabelId}
            label="Type"
            value={typeSelectValue}
            onChange={(e) =>
              !isEditMode && setType((e.target.value || "text") as TransactionCustomFieldType)
            }
            renderValue={(v) => (v === "attachment" ? "Attachment" : "Text")}
            disabled={saving || isEditMode}
          >
            <MenuItem value="text">Text</MenuItem>
            <MenuItem value="attachment">Attachment</MenuItem>
          </Select>
          <CannotChangeLaterIcon
            tooltipMessage="Type cannot be changed after the field is created."
            position="select"
            sx={{ mt: 0, top: "28px", transform: "translateY(-50%)" }}
          />
        </FormControl>
        <FormControl fullWidth variant="outlined" size="medium" sx={{ position: "relative" }}>
          <InputLabel id="transaction-type-label">For transaction type</InputLabel>
          <Select<TransactionCustomFieldScopeType>
            labelId="transaction-type-label"
            label="For transaction type"
            value={transactionType}
            onChange={(e) => setTransactionType((e.target.value || "expense") as TransactionCustomFieldScopeType)}
            disabled={saving || isEditMode}
            renderValue={(v) => v.charAt(0).toUpperCase() + v.slice(1)}
          >
            {SCOPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
          <CannotChangeLaterIcon
            tooltipMessage="Transaction type cannot be changed after the field is created."
            position="select"
            sx={{ mt: 0, top: "28px", transform: "translateY(-50%)" }}
          />
          <FormHelperText>
            This field will appear only when creating or editing {transactionType} transactions. Same field name is allowed for different types.
          </FormHelperText>
          {isEditMode && (
            <FormHelperText sx={{ color: "text.secondary" }}>
              Transaction type cannot be changed after creation.
            </FormHelperText>
          )}
        </FormControl>
        <FormControl fullWidth variant="outlined" size="medium">
          <InputLabel id="transaction-field-icon-label">Icon</InputLabel>
          <Select<TransactionCustomFieldIconName>
            labelId="transaction-field-icon-label"
            label="Icon"
            value={icon}
            onChange={(e) => setIcon((e.target.value || DEFAULT_ICON) as TransactionCustomFieldIconName)}
            disabled={saving}
            renderValue={(v) => {
              const opt = ICON_OPTIONS.find((o) => o.value === v);
              return opt ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <opt.Icon sx={{ fontSize: 20, color: "text.secondary" }} />
                  <span>{opt.label}</span>
                </Box>
              ) : (
                v
              );
            }}
          >
            {ICON_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <opt.Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
          <FormHelperText sx={{ color: "text.secondary" }}>
            You can change the icon anytime.
          </FormHelperText>
        </FormControl>
      </Stack>
    </StandardDialog>
  );
}
